import React, {
  ReactElement,
  ReactNode,
  ChangeEvent,
  MouseEvent,
  ComponentProps,
} from 'react';

import PropTypes from 'prop-types';

import { useControlled } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { Details, DetailsProps, Overlay, OverlayProps, Image } from './layout';

const defaultRenderImage = (props: ComponentProps<typeof Image>) => (
  <Image {...props} />
);
const defaultRenderDetails = (props: ComponentProps<typeof Details>) => (
  <Details {...props} />
);
const defaultRenderOverlay = (props: ComponentProps<typeof Overlay>) => (
  <Overlay {...props} />
);

type TileProps = StylableComponent<HTMLLabelElement> &
  DetailsProps & {
    image?: string;
    renderImage?: (props: ComponentProps<typeof Image>) => ReactElement | null;
    renderOverlay?: (props: OverlayProps) => ReactElement | null;
    width?: number;
    imageHeight?: number;
    badge?: ReactNode;
    selected?: boolean;
    defaultSelected?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
    renderDetails?: (props: DetailsProps) => ReactElement | null;
  };

/**
 * Tiles are components that display information related to a single subject or destination.
 * They should be used to represent items in grid layouts, such as files and folders on an application start page.
 */
const Tile = React.forwardRef<HTMLLabelElement, TileProps>(
  (
    {
      image,
      renderImage = defaultRenderImage,
      width = 178,
      imageHeight = 130,
      badge,
      selected: selectedProp,
      defaultSelected = false,
      onChange: onChangeProp,
      title,
      subtitle,
      tertiaryText,
      renderIcons,
      renderDetails = defaultRenderDetails,
      renderOverlay = defaultRenderOverlay,
      renderActions,
      titleStyle,
      subtitleStyle,
      metadataStyle,
      style,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const tileTokens = theme.tokens?.component?.tile;
    const { container, image: imageTokens, overlay } = tileTokens ?? {};

    const [selected, setSelected] = useControlled<boolean>(
      defaultSelected ?? false,
      selectedProp,
    );

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      setSelected(event.target.checked);
      onChangeProp?.(event);
    };

    const imageBorderColor = selected
      ? imageTokens?.borderColor?.selected
      : imageTokens?.borderColor?.default;

    const hoverBorderColor = imageTokens?.borderColor?.hover;

    const hoverContainerStyles = selected
      ? {
          boxShadow: container?.['selected-hover']?.boxShadow,
          backgroundColor: container?.['selected-hover']?.backgroundColor,
        }
      : {
          boxShadow: container?.hover?.boxShadow,
          backgroundColor: container?.hover?.backgroundColor,
        };

    return (
      <label
        ref={ref}
        css={[
          {
            position: 'relative',
            display: 'block',
            width,
            borderRadius: container?._?.borderRadius,
            boxShadow: container?._?.boxShadow,
            backgroundColor: container?._?.backgroundColor,
            borderWidth: container?._?.borderWidth,
            borderStyle: container?._?.borderStyle,
            borderColor: container?._?.borderColor,
            transition: container?._?.transition,
            ...(selected && {
              boxShadow: container?.selected?.boxShadow,
              backgroundColor: container?.selected?.backgroundColor,
              borderWidth: container?.selected?.borderWidth,
              borderStyle: container?.selected?.borderStyle,
              borderColor: container?.selected?.borderColor,
            }),
            'div.button': {
              display: 'none',
            },
            '&:hover,&:focus-within': {
              ...hoverContainerStyles,
              'div.image': {
                borderColor: hoverBorderColor,
              },
              'div.details': {
                borderColor: hoverBorderColor,
              },
              'div.checkbox': {
                opacity: 1,
              },
              'div.button': {
                display: 'block',
              },
            },
          },
          style,
        ]}
        {...props}
      >
        <div
          css={[
            {
              position: 'absolute',
              width: '100%',
              justifyContent: 'space-between',
              paddingTop: overlay?.paddingTop,
              paddingRight: overlay?.paddingRight,
              paddingBottom: overlay?.paddingBottom,
              paddingLeft: overlay?.paddingLeft,
              display: 'flex',
              zIndex: 1,
            },
          ]}
        >
          {renderOverlay({ defaultSelected, onChange, selected, badge })}
        </div>
        <div
          className="image"
          css={[
            {
              overflow: 'hidden',
              height: imageHeight,
              background: imageTokens?.backgroundColor,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              borderWidth: imageTokens?.borderWidth,
              borderStyle: imageTokens?.borderStyle,
              borderColor: imageBorderColor,
              boxSizing: 'border-box',
              borderTopLeftRadius: imageTokens?.borderTopLeftRadius,
              borderTopRightRadius: imageTokens?.borderTopRightRadius,
              borderBottomLeftRadius: imageTokens?.borderBottomLeftRadius,
              borderBottomRightRadius: imageTokens?.borderBottomRightRadius,
            },
          ]}
        >
          {renderImage && renderImage({ src: image, alt: '' })}
        </div>
        {renderDetails({
          className: 'details',
          title,
          subtitle,
          tertiaryText,
          renderIcons,
          renderActions,
          selected,
          titleStyle,
          subtitleStyle,
          metadataStyle,
        })}
      </label>
    );
  },
);

Tile.displayName = 'Tile';

Tile.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Tile title */
  title: PropTypes.string,
  /** Tile subtitle */
  subtitle: PropTypes.string,
  /** Tile tertiary text */
  tertiaryText: PropTypes.string,
  /** Render icons function */
  renderIcons: PropTypes.func,
  /** Render actions.  Generally used to render a three dot ActionButton */
  renderActions: PropTypes.func,
  /** URL to a tile image */
  image: PropTypes.string,
  /** The width of the Tile */
  width: PropTypes.number,
  /** The height of the Tile image*/
  imageHeight: PropTypes.number,
  /** The label content */
  badge: PropTypes.any,
  /** If Tile is selected. */
  selected: PropTypes.bool,
  /** If Tile is selected by default */
  defaultSelected: PropTypes.bool,
  /** onChange event handler. */
  onChange: PropTypes.func,
  /** onClick event handler. */
  onClick: PropTypes.func,
  /** Render custom image. */
  renderImage: PropTypes.func,
  /** Render custom tile overlay content. */
  renderOverlay: PropTypes.func,
  /** Render custom tile details content. */
  renderDetails: PropTypes.func,
  /** Style applied to the title container element, merged after token defaults. */
  titleStyle: stylePropType,
  /** Style applied to the subtitle container element, merged after token defaults. */
  subtitleStyle: stylePropType,
  /** Style applied to the metadata container element (tertiaryText + icons), merged after token defaults. */
  metadataStyle: stylePropType,
};

export default Object.assign(Tile, {
  Details,
  Image,
});
